/*
 * @Author: Ryan
 * @Date: 2026-01-08
 * @Description: 语言选择器组件
 */
import { Select } from 'antd';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import { useTranslate } from '@/hooks/useTranslate';
import { useTranslationConfig } from '@/hooks/useTranslationConfig';
import useSubtitleTranslationStore from '@/store/SubtitleTranslation';

import styles from './LanguageSelector.module.scss';

interface LanguageSelectorProps {
  sourceLanguageLabel: string;
  sourceLanguageDefault: string;
  targetLanguageLabel: string;
  targetLanguageDefault: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  sourceLanguageLabel,
  sourceLanguageDefault,
  targetLanguageLabel,
  targetLanguageDefault,
}) => {
  const [isClient, setIsClient] = useState(false);
  const { t } = useTranslate();
  const [originLangOptions, targetLangOptions] = useTranslationConfig(t);

  // 确保只在客户端渲染
  useEffect(() => {
    setIsClient(true);
  }, []);

  // 过滤掉自动检测选项
  const filteredOriginLang = originLangOptions.filter(
    (lang: any) => lang.value !== 'autoDetectLang',
  );

  // 从 SubtitleTranslationStore 获取状态
  const language = useSubtitleTranslationStore((state) => state.language);
  const setLanguage = useSubtitleTranslationStore((state) => state.setLanguage);

  // 找到默认语言的 value
  const findLangValue = (options: any[], defaultName: string) => {
    const found = options.find(
      (opt: any) => opt.name?.toLowerCase() === defaultName?.toLowerCase(),
    );
    return found?.value || options[0]?.value;
  };

  // 初始化默认值（仅在首次加载时）
  useEffect(() => {
    if (!language.origin || language.origin === 'autoDetectLang') {
      const defaultOrigin = findLangValue(filteredOriginLang, sourceLanguageDefault);
      const defaultTarget = findLangValue(targetLangOptions, targetLanguageDefault);
      setLanguage({
        origin: defaultOrigin,
        target: defaultTarget ? [defaultTarget] : [],
      });
    }
  }, []);

  const sourceValue =
    language.origin || findLangValue(filteredOriginLang, sourceLanguageDefault);
  const targetValueArray = language.target?.length
    ? language.target
    : [findLangValue(targetLangOptions, targetLanguageDefault)].filter(Boolean);

  const handleSourceChange = (value: string) => {
    setLanguage({
      origin: value,
      target: targetValueArray,
    });
  };

  const handleTargetChange = (values: string[]) => {
    const selectedValues = Array.isArray(values) ? values : [values];
    setLanguage({
      origin: sourceValue,
      target: selectedValues,
    });
  };

  // 自定义渲染选项（带国旗图标）
  const renderOption = (option: any) => (
    <div className={styles.optionItem}>
      {option.icon && (
        <Image
          src={option.icon}
          alt={option.name}
          width={20}
          height={14}
          className={styles.flagIcon}
        />
      )}
      <span>{option.label}</span>
    </div>
  );

  // 服务端渲染时显示占位符，避免 Hydration 错误
  if (!isClient) {
    return (
      <div className={styles.languageSelector}>
        <div className={styles.languageGroup}>
          <label className={styles.label}>{sourceLanguageLabel}</label>
          <div className={styles.selectPlaceholder} />
        </div>
        <div className={styles.languageGroup}>
          <label className={styles.label}>{targetLanguageLabel}</label>
          <div className={styles.selectPlaceholder} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.languageSelector}>
      {/* Source Language */}
      <div className={styles.languageGroup}>
        <label className={styles.label}>{sourceLanguageLabel}</label>
        <Select
          className={styles.select}
          size="large"
          value={sourceValue}
          onChange={handleSourceChange}
          options={filteredOriginLang.map((opt: any) => ({
            value: opt.value,
            label: renderOption(opt),
            searchLabel: opt.name,
          }))}
          showSearch
          filterOption={(input, option) =>
            (option?.searchLabel as string)?.toLowerCase().includes(input.toLowerCase())
          }
          classNames={{
            popup: {
              root: styles.selectPopup,
            },
          }}
        />
      </div>

      {/* Target Language */}
      <div className={styles.languageGroup}>
        <label className={styles.label}>{targetLanguageLabel}</label>
        <Select
          className={styles.select}
          size="large"
          value={targetValueArray}
          onChange={handleTargetChange}
          options={targetLangOptions.map((opt: any) => ({
            value: opt.value,
            label: renderOption(opt),
            searchLabel: opt.name,
          }))}
          showSearch
          filterOption={(input, option) =>
            (option?.searchLabel as string)?.toLowerCase().includes(input.toLowerCase())
          }
          classNames={{
            popup: {
              root: styles.selectPopup,
            },
          }}
          mode="multiple"
          maxCount={2}
        />
      </div>
    </div>
  );
};

export default LanguageSelector;
